import React from "react";
import { Label } from "reactstrap";
import { useTranslation } from "react-i18next";
import { AccordionComponent } from "../../../CommonElements/Accordion";
import { usePermissionLogic } from "./hooks/usePermissionLogic";
import { renderPermissions } from "./utils/permissionRenderer";

const PermissionSelector = ({
  systemId,
  permissionsData,
  loading,
  selectedPermissions,
  onPermissionChange,
  error,
  control = null,
  hideLabel = false,
}) => {
  const { t } = useTranslation();
  const {
    handlePermissionChange,
    handleParentChange,
    getAllPermissionIds,
    areAllChildrenSelected,
    areAnyChildrenSelected,
  } = usePermissionLogic({
    selectedPermissions,
    onPermissionChange,
  });

  if (!systemId) {
    return null;
  }

  // Skeleton loading component
  const PermissionSkeleton = () => (
    <div className="permission-display-skeleton">
      <div className="skeleton-accordion">
        {[1, 2].map((item) => (
          <div key={item} className="skeleton-accordion-item">
            <div className="skeleton skeleton-accordion-header"></div>
            <div className="skeleton-permission-list">
              {[1, 2].map((permItem) => (
                <div key={permItem} className="skeleton-permission-item">
                  <div className="skeleton skeleton-checkbox"></div>
                  <div className="skeleton skeleton-permission-text"></div>
                </div>
              ))}
            </div>
            <div className="skeleton-nested-section">
              <div className="skeleton skeleton-nested-header"></div>
              {[1, 2].map((nestedItem) => (
                <div key={nestedItem} className="skeleton-permission-item">
                  <div className="skeleton skeleton-checkbox"></div>
                  <div className="skeleton skeleton-permission-text"></div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Friendly no permissions UI
  const NoPermissionsUI = () => (
    <div className="text-center py-4">
      <div className="mb-3">
        <i
          className="fa fa-shield-alt text-muted"
          style={{ fontSize: "3rem" }}
        ></i>
      </div>
      <h5 className="text-muted mb-2">{t("NO_PERMISSIONS_AVAILABLE")}</h5>
      <p className="text-muted small mb-0">{t("NO_PERMISSIONS_DESCRIPTION")}</p>
    </div>
  );

  // Show skeleton while loading and no data yet
  if (loading && !permissionsData?.data?.data?.length) {
    return (
      <>
        {!hideLabel && <Label className="H2 required">{t("GROUP_PERMISSIONS")}</Label>}
        {error && (
          <div className="text-danger d-block mb-2">{error.message}</div>
        )}
        <div className="container-fluid">
          <PermissionSkeleton />
        </div>
      </>
    );
  }

  // Show friendly UI when data is loaded but no permissions available
  if (!loading && !permissionsData?.data?.data?.length) {
    return (
      <>
        {!hideLabel && <Label className="H2 required">{t("GROUP_PERMISSIONS")}</Label>}
        {error && (
          <div className="text-danger d-block mb-2">{error.message}</div>
        )}
        <div className="container-fluid">
          <NoPermissionsUI />
        </div>
      </>
    );
  }

  const accordionItems = renderPermissions({
    nodes: permissionsData.data.data,
    selectedPermissions,
    handlePermissionChange,
    handleParentChange,
    getAllPermissionIds,
    areAllChildrenSelected,
    areAnyChildrenSelected,
    control,
    mode: "form",
    col: 12,
  });

  return (
    <>
      {!hideLabel && <Label className="H2 required">{t("GROUP_PERMISSIONS")}</Label>}
      {error && <div className="text-danger d-block mb-2">{error.message}</div>}
      <div className="container-fluid">
        <AccordionComponent className="vstack gap-2" items={accordionItems} />
      </div>
    </>
  );
};

export default PermissionSelector;
